/**
 * API-хелпер для Astranum
 * Обёртка над fetch с автоматическим добавлением токена,
 * refresh при 401 и типизированными методами.
 *
 * Фаза 1.1: HttpOnly cookies. Токены больше не хранятся в localStorage —
 * браузер отправляет их автоматически через credentials: 'include'.
 * Authorization header используется как fallback для обратной совместимости.
 */

import { handleApiError } from './utils/apiErrors';

const BASE_URL = import.meta.env.VITE_API_URL || '/api';

interface ApiResponse<T> {
  success: boolean;
  data?: T;
  error?: string;
  message?: string;
  cached?: boolean;
  generatedAt?: string;
  expiresAt?: string;
}

let isRefreshing = false;
let refreshPromise: Promise<boolean> | null = null;

// Legacy fallback: читаем токен из localStorage (старые сессии до миграции)
function getLegacyToken(): string | null {
  return localStorage.getItem('token');
}

function clearLegacyTokens(): void {
  localStorage.removeItem('token');
  localStorage.removeItem('refreshToken');
}

async function refreshToken(): Promise<boolean> {
  if (isRefreshing && refreshPromise) {
    return refreshPromise;
  }

  isRefreshing = true;
  refreshPromise = (async () => {
    try {
      // refreshToken берётся из HttpOnly cookie автоматически (credentials: 'include')
      const res = await fetch(`${BASE_URL}/auth/refresh`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        // body не нужен — сервер читает refreshToken из cookie
      });

      if (!res.ok) {
        clearLegacyTokens();
        return false;
      }

      const data = await res.json();
      return data.success === true;
    } catch {
      clearLegacyTokens();
      return false;
    } finally {
      isRefreshing = false;
      refreshPromise = null;
    }
  })();

  return refreshPromise;
}

function getAuthHeaders(): Record<string, string> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  };
  // Fallback: Authorization header для старых сессий (пока cookie не установлены)
  const legacyToken = getLegacyToken();
  if (legacyToken) {
    headers['Authorization'] = `Bearer ${legacyToken}`;
  }
  return headers;
}

async function request<T>(
  method: string,
  url: string,
  body?: unknown,
): Promise<ApiResponse<T>> {
  const fullUrl = url.startsWith('http') ? url : `${BASE_URL}${url}`;

  const controller = new AbortController();
  const timeoutMs = 90_000;
  const timer = setTimeout(() => controller.abort(), timeoutMs);

  let res: Response;
  try {
    res = await fetch(fullUrl, {
      method: method.toUpperCase(),
      headers: getAuthHeaders(),
      credentials: 'include',
      body: body !== undefined ? JSON.stringify(body) : undefined,
      signal: controller.signal,
    });
  } catch (err) {
    clearTimeout(timer);
    if (err instanceof Error && err.name === 'AbortError') {
      return { success: false, error: 'Превышено время ожидания (90с). Попробуйте ещё раз.' };
    }
    return {
      success: false,
      error: handleApiError(err, 'Сетевая ошибка'),
    };
  }

  clearTimeout(timer);

  // Попытка refresh при 401
  if (res.status === 401) {
    const refreshed = await refreshToken();
    if (refreshed) {
      // Повторный запрос (новый accessToken уже в cookie)
      try {
        const retryController = new AbortController();
        const retryTimer = setTimeout(() => retryController.abort(), timeoutMs);
        res = await fetch(fullUrl, {
          method,
          headers: getAuthHeaders(),
          credentials: 'include',
          body: body !== undefined ? JSON.stringify(body) : undefined,
          signal: retryController.signal,
        });
        clearTimeout(retryTimer);
      } catch (err) {
        return {
          success: false,
          error: handleApiError(err, 'Сетевая ошибка'),
        };
      }
    } else {
      return {
        success: false,
        error: 'Сессия истекла. Войдите снова.',
      };
    }
  }

  let data: ApiResponse<T>;
  try {
    data = await res.json();
  } catch {
    return {
      success: false,
      error: 'Некорректный ответ сервера',
    };
  }

  if (!res.ok) {
    return {
      success: false,
      error: data.error || data.message || `Ошибка ${res.status}`,
    };
  }

  return data;
}

// ============================================================
// Типы для натальной карты (Swiss Ephemeris формат)
// ============================================================

export interface NatalChartPlanet {
  name: string;
  nameRu: string;
  symbol: string;
  sign: string;
  signRu: string;
  degree: number;
  minutes: number;
  degreeFormatted: string;
  longitude: number;
  degreeInSign: number;
  house: number;
  retrograde: boolean;
}

export interface NatalChartHouse {
  number: number;
  sign: string;
  signRu: string;
  degree: number;
  minutes: number;
  degreeFormatted: string;
  longitude: number;
  degreeInSign: number;
}

export interface NatalChartAspect {
  planet1: string;
  planet2: string;
  type: string;
  orb: number;
  applying: boolean;
}

export interface NatalChartResponse {
  sunSign: string;
  sunSignRu: string;
  moonSign: string;
  moonSignRu: string;
  ascendant: string;
  ascendantRu: string;
  ascendantDegree: string;
  ascendantLongitude: number;
  mc: string;
  mcRu: string;
  mcLongitude: number;
  planets: NatalChartPlanet[];
  houses: NatalChartHouse[];
  aspects: NatalChartAspect[];
  houseRulers: Array<{
    house: number;
    ruler: string;
    rulerSign: string;
    rulerSignRu: string;
    rulerHouse: number;
  }>;
  engine: string;
  julianDay: number;
}

export interface AllCalculationsData {
  natalChart: NatalChartResponse | null;
  numerology: unknown;
  calculationsReady: boolean;
}

// ============================================================
// Типы для прогнозов (Фаза 2)
// ============================================================

export interface ForecastResponse {
  forecast?: string;
  answer?: string;
  days?: Array<{ date: string; summary: string }>;
  highlights?: string[];
  recommendations?: string[];
  warnings?: string[];
  // Legacy поля
  type?: string;
  title?: string;
  rating?: number;
  tone?: string;
  summary?: string;
  aiInterpretation?: string;
  generatedAt?: string;
  cached?: boolean;
  keyTransits?: string[];
  areaForecasts?: Record<string, unknown>[];
}

/**
 * Ответ от POST /api/forecast/execute
 * Содержит данные прогноза + мета-информацию о кэше
 */
export interface ForecastExecuteResponse {
  success: boolean;
  data: ForecastResponse;
  cached: boolean;
  generatedAt: string;
  expiresAt?: string;
  error?: string;
}

// ============================================================
// API клиент
// ============================================================

export const api = {
  get<T>(url: string): Promise<ApiResponse<T>> {
    return request<T>('GET', url);
  },

  post<T>(url: string, body?: unknown): Promise<ApiResponse<T>> {
    return request<T>('POST', url, body);
  },

  put<T>(url: string, body?: unknown): Promise<ApiResponse<T>> {
    return request<T>('PUT', url, body);
  },

  delete<T>(url: string): Promise<ApiResponse<T>> {
    return request<T>('DELETE', url);
  },

  patch<T>(url: string, body?: unknown): Promise<ApiResponse<T>> {
    return request<T>('PATCH', url, body);
  },
};

// ============================================================
// Типизированные хелперы для натальной карты
// ============================================================

/** Получить натальную карту пользователя (Swiss Ephemeris) */
export async function fetchNatalChart(
  userId: string,
): Promise<ApiResponse<NatalChartResponse>> {
  return api.get<NatalChartResponse>(`/calculations/natal-chart/${userId}`);
}

/** Получить все расчёты пользователя */
export async function fetchAllCalculations(): Promise<ApiResponse<AllCalculationsData>> {
  return api.get<AllCalculationsData>('/calculations/all');
}

/** Рассчитать натальную карту (POST) */
export async function calculateNatalChart(body: {
  birthDate: string;
  birthTime?: string;
  birthPlace: {
    city?: string;
    country?: string;
    latitude: number;
    longitude: number;
    timezone?: string;
    timezoneOffset?: number;
  };
  houseSystem?: string;
}): Promise<ApiResponse<NatalChartResponse>> {
  return api.post<NatalChartResponse>('/calculations/natal-chart', body);
}

/** AI-интерпретация натальной карты */
export async function interpretNatalChart(body: {
  chart: NatalChartResponse;
  profile: {
    full_name: string;
    birth_date: string;
    birth_time?: string;
    birth_place: string;
  };
}): Promise<ApiResponse<{ interpretation: string }>> {
  return api.post<{ interpretation: string }>('/calculations/natal-chart/interpret', body);
}

export default api;
export type { ApiResponse };
export { handleApiError } from './utils/apiErrors';
export type { ForecastExecuteResponse };
